import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "./utils/firebase";
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, fetchSignInMethodsForEmail, signInWithEmailAndPassword } from "firebase/auth";
import { useMfaSignIn } from './utils/useMfaSignIn';
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import logo from "./assets/featherglass-sq-logo.png";
import { companyName } from "./globals";
import { buildNewUserSettings } from "./config/userSettingsDefaults";
import { getDefaultPreset } from "./utils/firestoreService";
import { getFirebaseErrorMessage } from "./utils/errorHandler";
import { firstLogin } from "./utils/loginUtils";
import { validatePassword, getPasswordStrength, getPasswordStrengthLabel, getPasswordRequirements } from "./utils/passwordValidator";
import { CircleCheck, CircleX, Eye, EyeClosed } from "lucide-react";
import GoogleIcon from "@mui/icons-material/Google";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { logAuditEventAsync, AuditActions } from "./utils/auditLogger";
import { completeSignup } from "./utils/signupService";
import { ROLE_LABELS } from "./utils/roleUtils";
import billingPolicy from './config/billingPolicy.json';
import { requestEmailVerification } from "./utils/verificationService";
import {
  clearPendingSignupFormData,
  clearPendingSignupState,
  readPendingSignupFormData,
  writePendingSignupFormData,
} from "./utils/pendingSignupStorage";

const Signup = () => {
  const { signInWithMfa, mfaPrompt } = useMfaSignIn();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const displayName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [messageType, setMessageType] = useState("error");
  const [validatedCompany, setValidatedCompany] = useState(null);
  const [isInviteCodeValid, setIsInviteCodeValid] = useState(false);

  // Invite token mode (from /invite/:token redirect)
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [isInviteMode, setIsInviteMode] = useState(false);
  const [isEmailVerificationSent, setIsEmailVerificationSent] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);

  // Stripe payment states
  const [stripePrices, setStripePrices] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [stripeSessionData, setStripeSessionData] = useState(null);
  const [isGoogleSignupFlow, setIsGoogleSignupFlow] = useState(false);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isInviteCodeLoading, setIsInviteCodeLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Validation states
  const [emailValidation, setEmailValidation] = useState({ isValid: null, message: "" });
  const [passwordValidation, setPasswordValidation] = useState({ isValid: null, message: "", strength: 0 });
  const [confirmPasswordValidation, setConfirmPasswordValidation] = useState({ isValid: null, message: "" });
  const [phoneValidation, setPhoneValidation] = useState({ isValid: null, message: "" });
  const [inviteCodeValidation, setInviteCodeValidation] = useState({ isValid: null, message: "" });

  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // URL params for Stripe callback
  const [searchParams] = useSearchParams();

  // Refs for auto-focus
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const phoneRef = useRef(null);
  const inviteCodeRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);


  const navigate = useNavigate();

  // Handle Stripe checkout callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (!billingPolicy.enabled) {
      clearPendingSignupState();
      if (sessionId || success || canceled) setErrorMessage(billingPolicy.unavailableMessage);
      return;
    }

    if (canceled === 'true') {
      clearPendingSignupState();
      setErrorMessage('Payment was canceled. Please try again.');
      setShowPlanSelection(true);
      return;
    }

    if (success === 'true' && sessionId) {
      // Fetch session data and proceed with account creation
      const fetchSessionData = async () => {
        try {
          const response = await fetch(`/api/stripe/checkout-session/${sessionId}`);
          const data = await response.json();
          if (!response.ok || !data.customerId) {
            throw new Error(data.error || 'Payment session could not be verified');
          }

          setStripeSessionData({ ...data, sessionId });
          const savedFormData = readPendingSignupFormData();

          // Check if this is a Google signup flow
          const googleSignupPending = sessionStorage.getItem('googleSignupPending');
          if (googleSignupPending) {
            try {
              const googleData = JSON.parse(googleSignupPending);
              setEmail(googleData.email || '');
              setEmailValidation({ isValid: true, message: '' });
              setFirstName(savedFormData?.firstName || '');
              setLastName(savedFormData?.lastName || '');
              setIsInviteCodeValid(true);
              setInviteCodeValidation({ isValid: true, message: '' });
              setIsGoogleSignupFlow(true);
              setShowPlanSelection(false);
              // Restore invite code from form data if available
              if (savedFormData) {
                setInviteCode(savedFormData.inviteCode || '');
                setPhoneNumber(savedFormData.phoneNumber || '');
                // Re-validate invite code server-side to restore validatedCompany
                if (savedFormData.inviteCode) {
                  try {
                    const codeRes = await fetch('/api/validate-invite-code', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ code: savedFormData.inviteCode }),
                    });
                    const codeData = await codeRes.json();
                    if (codeData.valid) setValidatedCompany(codeData.company);
                  } catch (e) { /* non-blocking */ }
                }
              }
              setSuccess('Payment successful! Click "Sign Up with Google" to complete your account.');
              return;
            } catch {
              sessionStorage.removeItem('googleSignupPending');
            }
          }

          // Email/password checkout resumes only non-secret fields. The user must
          // enter a fresh password after returning from Stripe.
          if (savedFormData) {
            setEmail(savedFormData.email || data.customerEmail || '');
            setFirstName(savedFormData.firstName || '');
            setLastName(savedFormData.lastName || '');
            setPhoneNumber(savedFormData.phoneNumber || '');
            setInviteCode(savedFormData.inviteCode || '');
            setPassword('');
            setConfirmPassword('');
            setIsInviteCodeValid(true);
            setShowPlanSelection(false);
            // Re-validate invite code server-side to restore validatedCompany
            if (savedFormData.inviteCode) {
              try {
                const codeRes = await fetch('/api/validate-invite-code', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ code: savedFormData.inviteCode }),
                });
                const codeData = await codeRes.json();
                if (codeData.valid) setValidatedCompany(codeData.company);
              } catch (e) { /* non-blocking */ }
            }
            if (savedFormData.email) {
              setEmailValidation({ isValid: true, message: '' });
            }
            setPasswordValidation({ isValid: null, message: '', strength: 0 });
            setConfirmPasswordValidation({ isValid: null, message: '' });
            const phoneDigits = (savedFormData.phoneNumber || '').replace(/\D/g, '');
            setPhoneValidation({
              isValid: phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith('1')),
              message: '',
            });
            setSuccess('Payment successful! For your security, re-enter your password to create your account.');
          } else {
            // The paid session is valid, but the same-tab resume data is gone.
            // Let the user re-enter their invite code instead of guessing company data.
            setEmail(data.customerEmail || '');
            if (data.customerEmail) {
              setEmailValidation({ isValid: true, message: '' });
            }
            setShowPlanSelection(false);
            setError('Payment was verified, but your signup details expired. Re-enter your invite code to continue.');
          }
        } catch (error) {
          console.error('Error fetching session data:', error);
          clearPendingSignupState();
          setErrorMessage('Error verifying payment. Please contact support.');
        }
      };
      fetchSessionData();
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check for invite token in URL params
        const inviteParam = searchParams.get('invite');
        if (inviteParam) {
          try {
            const inviteRes = await fetch(`/api/invitations/validate/${inviteParam}`);
            const inviteResult = await inviteRes.json();
            if (inviteResult.valid) {
              setInviteToken(inviteParam);
              setInviteData(inviteResult.invitation);
              setIsInviteMode(true);
              setIsInviteCodeValid(true);
              setEmail(inviteResult.invitation.email);
              setEmailValidation({ isValid: true, message: "" });
              // Set validated company from invite data
              setValidatedCompany({
                id: inviteResult.invitation.companyId,
                requiresPayment: false, // Invited users don't pay
              });
            }
          } catch (inviteErr) {
            console.error("Failed to validate invite token:", inviteErr);
          }
        }

        // Fetch Stripe prices
        if (!billingPolicy.enabled) return;
        const pricesResponse = await fetch('/api/stripe/prices');
        const pricesData = await pricesResponse.json();
        if (pricesData.prices) {
          setStripePrices(pricesData.prices);
          // Default to first (cheapest) price
          if (pricesData.prices.length > 0) {
            setSelectedPrice(pricesData.prices[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchData();

  }, [navigate]);

  // Auto-focus next field when invite code is validated
  useEffect(() => {
    if (isInviteCodeValid && emailRef.current) {
      emailRef.current.focus();
    }
  }, [isInviteCodeValid]);

  const checkUserExists = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      const exists = userDoc.exists();
      return exists;
    } catch (error) {
      console.error("Error checking if user exists:", error);
      // Conservative fallback - assume user doesn't exist to avoid blocking signup
      return false;
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phoneNumber) => {
    const phoneDigits = phoneNumber.replace(/\D/g, "");
    return phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith("1"));
  };

  const formatPhoneNumber = (phoneNumber) => {
    const phoneDigits = phoneNumber.replace(/\D/g, "");
    if (phoneDigits.length === 10) {
      return `+1${phoneDigits}`;
    } else if (phoneDigits.length === 11 && phoneDigits.startsWith("1")) {
      return `+${phoneDigits}`;
    } else {
      throw new Error("Invalid phone number format");
    }
  };

  // Using comprehensive password validator utility

  // Check if company requires payment (controlled per invite code in admin)
  const requiresPayment = (company) => {
    return company?.requiresPayment === true;
  };

  const provisionProfile = async (user, phone) => {
    const preset = await getDefaultPreset('user');
    return completeSignup(user, {
      ...(isInviteMode ? { inviteToken } : { inviteCode }),
      ...(stripeSessionData?.sessionId ? { checkoutSessionId: stripeSessionData.sessionId } : {}),
      displayName,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneNumber: phone || '',
      settings: buildNewUserSettings(preset?.settings),
    });
  };

  // Handle Stripe checkout redirect
  const handleStripeCheckout = async () => {
    if (!billingPolicy.enabled) {
      setErrorMessage(billingPolicy.unavailableMessage);
      return;
    }
    if (!selectedPrice || !email) {
      setErrorMessage('Please select a plan and enter your email');
      return;
    }

    // Check if this is a Google signup flow (skip password validation)
    const googleSignupPending = sessionStorage.getItem('googleSignupPending');
    const isGoogleFlow = !!googleSignupPending;

    // Validate required fields - skip password for Google users
    if (!isGoogleFlow) {
      if (!passwordValidation.isValid) {
        setErrorMessage('Please enter a valid password before proceeding to payment');
        return;
      }

      if (!confirmPasswordValidation.isValid) {
        setErrorMessage('Passwords must match before proceeding to payment');
        return;
      }

      if (!phoneValidation.isValid) {
        setErrorMessage('Please enter a valid phone number before proceeding to payment');
        return;
      }
    }

    setIsStripeLoading(true);
    try {
      // Check if email already exists in Firebase Auth BEFORE payment (skip for Google - they already authenticated)
      if (!isGoogleFlow) {
        const emailExists = await checkEmailExists(email);
        if (emailExists) {
          setErrorMessage('This email is already registered. Please log in instead.');
          setIsStripeLoading(false);
          return;
        }
      }

      // Persist only non-secret fields needed to resume after Stripe.
      writePendingSignupFormData({
        email,
        displayName,
        firstName,
        lastName,
        phoneNumber,
        inviteCode,
      });

      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: selectedPrice,
          email,
          inviteCode,
          displayName
        })
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      clearPendingSignupFormData();
      setErrorMessage('Error starting payment process. Please try again.');
    }
    setIsStripeLoading(false);
  };

  // Format price for display
  const formatPrice = (unitAmount, currency, interval) => {
    const amount = (unitAmount / 100).toFixed(2);
    const currencySymbol = currency === 'usd' ? '$' : currency.toUpperCase();
    return `${currencySymbol}${amount}/${interval}`;
  };

  // Real-time validation handlers
  const handleEmailChange = async (e) => {
    const value = e.target.value;
    setEmail(value);

    if (!value) {
      setEmailValidation({ isValid: null, message: "" });
      return;
    }

    if (!validateEmail(value)) {
      setEmailValidation({ isValid: false, message: "Please enter a valid email address" });
      return;
    }

    // Check if email exists
    try {
      const emailExists = await checkEmailExists(value);
      if (emailExists) {
        setEmailValidation({ isValid: false, message: "An account with this email already exists" });
      } else {
        setEmailValidation({ isValid: true, message: "Email is available" });
      }
    } catch (error) {
      setEmailValidation({ isValid: true, message: "Email format is valid" });
    }
  };

  const validateProfileName = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      (!firstName.trim() ? firstNameRef : lastNameRef).current?.focus();
      return false;
    }
    return true;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    if (!value) {
      setPasswordValidation({ isValid: null, message: "", strength: 0 });
      return;
    }

    const strength = getPasswordStrength(value, email);
    const isValid = validatePassword(value);
    const strengthInfo = getPasswordStrengthLabel(strength);

    let message = "";
    if (!isValid) {
      const requirements = getPasswordRequirements(value);
      const unmetReqs = requirements.filter((req) => !req.met);
      if (unmetReqs.length > 0) {
        message = `Missing: ${unmetReqs.map((req) => req.message.toLowerCase()).join(", ")}`;
      }
    } else {
      message = `${strengthInfo.label} password`;
    }

    setPasswordValidation({ isValid, message, strength });

    // Re-validate confirm password if it exists
    if (confirmPassword) {
      handleConfirmPasswordChange({ target: { value: confirmPassword } });
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (!value) {
      setConfirmPasswordValidation({ isValid: null, message: "" });
      return;
    }

    const isValid = value === password;
    const message = isValid ? "Passwords match" : "Passwords do not match";

    setConfirmPasswordValidation({ isValid, message });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);

    if (!value) {
      setPhoneValidation({ isValid: null, message: "" });
      return;
    }

    const isValid = validatePhoneNumber(value);
    const message = isValid ? "Phone number is valid" : "Please enter a valid 10-digit phone number";

    setPhoneValidation({ isValid, message });
  };

  const handleInviteCodeChange = (e) => {
    const value = e.target.value;
    setInviteCode(value);

    if (!value) {
      setInviteCodeValidation({ isValid: null, message: "" });
      return;
    }

    // Clear previous validation — code is only validated on submit
    if (inviteCodeValidation.isValid === false) {
      setInviteCodeValidation({ isValid: null, message: "" });
    }
  };

  // Handle Enter key navigation
  const handleKeyPress = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  const handleInviteCodeSubmit = async () => {
    setIsInviteCodeLoading(true);
    setError("");

    try {
      const response = await fetch('/api/validate-invite-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: inviteCode.trim() }),
      });

      const data = await response.json();

      if (data.code === 'BILLING_DISABLED' || (!billingPolicy.enabled && data.company?.requiresPayment === true)) {
        setValidatedCompany(null);
        setIsInviteCodeValid(false);
        setShowPlanSelection(false);
        setInviteCodeValidation({ isValid: false, message: billingPolicy.unavailableMessage });
        setError(billingPolicy.unavailableMessage);
        return;
      }

      if (data.valid && data.company) {
        setValidatedCompany(data.company);
        setIsInviteCodeValid(true);

        if (requiresPayment(data.company)) {
          setShowPlanSelection(true);
          setInviteCodeValidation({ isValid: true, message: "Please select a subscription plan to continue." });
        } else {
          setShowPlanSelection(false);
          setInviteCodeValidation({ isValid: true, message: "Welcome! Please complete your registration." });
        }
      } else {
        setError(getFirebaseErrorMessage({ code: 'validation/invalid-invite-code' }, "signup"));
        setInviteCodeValidation({ isValid: false, message: "Invalid invite code" });
      }
    } catch (error) {
      console.error("Invite code validation error:", error);
      setError("Unable to validate invite code. Please try again.");
      setInviteCodeValidation({ isValid: false, message: "Validation failed" });
    } finally {
      setIsInviteCodeLoading(false);
    }
  };

  const checkEmailExists = async (email) => {
    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      const exists = signInMethods.length > 0;
      return exists;
    } catch (error) {
      console.error("Error checking if email exists:", error);
      // Conservative fallback - assume email doesn't exist to allow signup attempt
      return false;
    }
  };

  const handleSignUp = async () => {
    if (!isInviteCodeValid || (!billingPolicy.enabled && requiresPayment(validatedCompany))) {
      setError(billingPolicy.unavailableMessage);
      return;
    }
    // Validate all fields before proceeding
    if (!validateProfileName()) return;
    if (!emailValidation.isValid) {
      setError(getFirebaseErrorMessage({ code: 'validation/invalid-email' }, "signup"));
      emailRef.current?.focus();
      return;
    }

    if (!passwordValidation.isValid) {
      setError(getFirebaseErrorMessage({ code: 'validation/weak-password' }, "signup"));
      passwordRef.current?.focus();
      return;
    }

    if (!confirmPasswordValidation.isValid) {
      setError(getFirebaseErrorMessage({ code: 'validation/password-mismatch' }, "signup"));
      confirmPasswordRef.current?.focus();
      return;
    }

    if (!phoneValidation.isValid) {
      setError(getFirebaseErrorMessage({ code: 'validation/invalid-phone' }, "signup"));
      phoneRef.current?.focus();
      return;
    }

    // If payment is required and we don't have Stripe session data, redirect to checkout
    const company = validatedCompany;
    if (requiresPayment(company) && !stripeSessionData) {
      setShowPlanSelection(true);
      setErrorMessage('Please complete payment before creating your account.');
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Set signup flag to prevent auth context interference
      sessionStorage.setItem("signupInProgress", "true");

      let user;
      try {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        user = credential.user;
      } catch (createError) {
        if (createError.code !== 'auth/email-already-in-use') throw createError;
        // Recover Auth-only accounts after an interrupted provisioning request.
        // The server returns completed profiles unchanged on retries.
        const credential = await signInWithMfa(() => signInWithEmailAndPassword(auth, email, password));
        user = credential.user;
      }

      let provisioned;
      try {
        provisioned = await provisionProfile(user, formatPhoneNumber(phoneNumber));
      } catch (profileError) {
        await auth.signOut();
        clearPendingSignupState();
        setError(profileError.message || 'Could not complete signup. Please try again.');
        return;
      }

      // Now send email verification
      let verificationSent = false;
      try {
        const verificationDelivery = await requestEmailVerification(user);
        if (!verificationDelivery.success) {
          throw new Error(verificationDelivery.error || "Verification email delivery failed");
        }
        setIsEmailVerificationSent(true);
        verificationSent = true;
        setSuccess("A verification email has been sent to your inbox. Please check your email and click the verification link to activate your account.");
        
        // Audit log: signup success
        logAuditEventAsync(AuditActions.AUTH_SIGNUP, {
          userId: user.uid,
          userEmail: email,
          companyId: provisioned.companyId,
          metadata: { signupType: isInviteMode ? 'invite' : 'email' },
        });
      } catch (verificationError) {
        console.error("Error sending verification email:", verificationError);
        setVerificationFailed(true);
        setError(getFirebaseErrorMessage({ code: 'firestore/verification-email-failed' }, "signup"));
      }

      // Clear signup flag and sign out the user
      setPassword("");
      setConfirmPassword("");
      clearPendingSignupState();
      await auth.signOut();
      setIsLoading(false);
      if (verificationSent) setTimeout(() => navigate("/"), 10000);
    } catch (error) {
      // Use centralized error handling
      const friendlyMessage = getFirebaseErrorMessage(error, "signup");

      // Special handling for email-already-in-use to show link
      if (error.code === "auth/email-already-in-use") {
        setError(friendlyMessage);
        setTimeout(() => {
          setError(
            <span>
              {friendlyMessage}{" "}
              <Link to="/" style={{ color: "var(--color-link)", textDecoration: "underline" }}>
                Click here to login
              </Link>
            </span>
          );
        }, 100);
        return;
      }

      setError(friendlyMessage);
    } finally {
      setIsLoading(false);
      sessionStorage.removeItem("signupInProgress");
      clearPendingSignupFormData();
    }
  };

  const handleGoogleSignUp = async () => {
    if (!billingPolicy.enabled && requiresPayment(validatedCompany)) {
      setError(billingPolicy.unavailableMessage);
      return;
    }
    const hasValidatedSignupPath = isInviteMode
      ? Boolean(inviteToken && inviteData)
      : inviteCodeValidation.isValid === true;

    if (!hasValidatedSignupPath) {
      setError(getFirebaseErrorMessage({ code: 'validation/missing-invite-code' }, "signup"));
      inviteCodeRef.current?.focus();
      return;
    }

    if (!validateProfileName()) return;

    // Check if payment is required and not completed
    const company = validatedCompany;
    if (requiresPayment(company) && !stripeSessionData) {
      // For Google signup with payment required:
      // 1. Authenticate with Google first to get email
      // 2. Then redirect to Stripe checkout
      setIsGoogleLoading(true);
      setError("");
      
      const provider = new GoogleAuthProvider();
      
      try {
        const result = await signInWithMfa(() => signInWithPopup(auth, provider));
        const googleUser = result.user;
        
        // Check if user already exists
        try {
          const userDoc = await getDoc(doc(db, "users", googleUser.uid));
          if (userDoc.exists()) {
            // Existing user - redirect to dashboard
            const userData = userDoc.data();
            if (userData.emailVerified) {
              setSuccess("Welcome back! Redirecting to dashboard...");
              setTimeout(() => navigate("/dashboard"), 1000);
              setIsGoogleLoading(false);
              return;
            }
          }
        } catch (checkError) {
          console.log("User document doesn't exist yet");
        }
        
        // Sign out temporarily - we'll complete signup after payment
        await auth.signOut();
        
        // Store Google user info for after payment
        sessionStorage.setItem('googleSignupPending', JSON.stringify({
          uid: googleUser.uid,
          email: googleUser.email,
          displayName: googleUser.displayName
        }));
        
        // Show plan selection with Google email pre-filled, hide password fields
        setEmail(googleUser.email);
        setEmailValidation({ isValid: true, message: '' });
        setIsGoogleSignupFlow(true);  // Hide password/phone fields immediately
        setShowPlanSelection(true);
        setSuccess(`Signed in as ${googleUser.email}. Please select a plan to continue.`);
        setIsGoogleLoading(false);
        return;
      } catch (error) {
        setIsGoogleLoading(false);
        const friendlyMessage = getFirebaseErrorMessage(error, "signup");
        setError(friendlyMessage);
        return;
      }
    }

    setIsGoogleLoading(true);
    setError("");

    // Set signup flag BEFORE initiating Google sign-in to prevent authContext race condition
    sessionStorage.setItem("signupInProgress", "true");

    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithMfa(() => signInWithPopup(auth, provider));
      const user = result.user;
      
      // Wait for auth state to fully propagate
      await user.getIdToken(true);

      // An invite is bound to one email address. Google lets the user choose
      // any signed-in account, so stop before creating a profile if it differs.
      if (
        isInviteMode
        && user.email?.trim().toLowerCase() !== inviteData?.email?.trim().toLowerCase()
      ) {
        await auth.signOut();
        clearPendingSignupState();
        setError(`Please continue with the invited Google account: ${inviteData.email}`);
        setIsGoogleLoading(false);
        return;
      }
      
      // Completion is idempotent, including recovery of older provisional
      // invitation profiles. No role, tenant, patient access, or billing writes
      // originate in the browser.
      try {
        const provisioned = await provisionProfile(
          user,
          phoneNumber && validatePhoneNumber(phoneNumber) ? formatPhoneNumber(phoneNumber) : ''
        );
        clearPendingSignupState();
        if (provisioned.created) {
          logAuditEventAsync(AuditActions.AUTH_SIGNUP, {
            userId: user.uid,
            userEmail: user.email,
            companyId: provisioned.companyId,
            metadata: { signupType: isInviteMode ? 'invite-google' : 'google' },
          });
        }
        const loggedIn = await firstLogin(user, 'google', setSuccess, navigate);
        if (loggedIn !== false) window.dispatchEvent(new Event('signup-completed'));
      } catch (profileError) {
        await auth.signOut();
        clearPendingSignupState();
        setError(profileError.message || 'Could not complete signup. Please try again.');
      }
    } catch (error) {
      // Popup/profile errors end this attempt; the in-memory Stripe result remains
      // available if the user retries without refreshing.
      clearPendingSignupState();
      
      // Use centralized error handling for popup errors
      const friendlyMessage = getFirebaseErrorMessage(error, "signup");
      setError(friendlyMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Helper function to set error messages
  const setError = (message) => {
    setErrorMessage(message);
    setMessageType("error");
  };

  // Helper function to set success messages
  const setSuccess = (message) => {
    setErrorMessage(message);
    setMessageType("success");
  };

  return (
    <div className="login-container">
      {isInitialLoading ? (
        <div className="loading-container">
          <CircularProgress size={40} />
          <p>Loading...</p>
        </div>
      ) : (
        <>
          <img src={logo} alt="Logo" className="logo-login" />

          {!isInviteCodeValid ? (
            <>
              <h3 className="title">Welcome! Please enter your invite code to get started.</h3>
              <div className="input-container">
                <input
                  ref={inviteCodeRef}
                  type="text"
                  value={inviteCode}
                  onChange={handleInviteCodeChange}
                  onKeyDown={(e) => handleKeyPress(e, null)}
                  placeholder="Enter your Invite Code"
                  className={`main-input ${inviteCodeValidation.isValid === false ? "input-error" : inviteCodeValidation.isValid === true ? "input-success" : ""}`}
                  disabled={isInviteCodeLoading}
                />
                <div className="input-validation">
                  {inviteCodeValidation.isValid === true && <CircleCheck className="validation-icon success" />}
                  {inviteCodeValidation.isValid === false && <CircleX className="validation-icon error" />}
                </div>
              </div>
              {inviteCodeValidation.message && <p className={`validation-message ${inviteCodeValidation.isValid ? "success" : "error"}`}>{inviteCodeValidation.message}</p>}
              {errorMessage && <p className="error-message">{errorMessage}</p>}
              <button onClick={handleInviteCodeSubmit} className="center-button primary" disabled={isInviteCodeLoading || !inviteCode}>
                {isInviteCodeLoading ? <CircularProgress size={20} color="inherit" /> : "Enter Invite Code"}
              </button>
            </>
          ) : (
            <>
              {isInviteMode && inviteData ? (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <h3 className="title">You've been invited to join {companyName}!</h3>
                  <p style={{ color: 'var(--color-text-light)', margin: '8px 0' }}>
                    Invited by <strong>{inviteData.inviterName}</strong>
                    {inviteData.role && (
                      <span> as <strong>{ROLE_LABELS[inviteData.role] || inviteData.role}</strong></span>
                    )}
                  </p>
                  {inviteData.message && (
                    <p style={{ fontStyle: 'italic', color: 'var(--color-text-light)', margin: '8px 16px', padding: '8px', backgroundColor: 'var(--color-background-alt)', borderRadius: '6px' }}>
                      "{inviteData.message}"
                    </p>
                  )}
                </div>
              ) : (
                <h3 className="title">Welcome to {companyName}. Please create your new account.</h3>
              )}

              {/* Plan Selection for paying users */}
              {billingPolicy.enabled && showPlanSelection && !stripeSessionData && (
                <div className="plan-selection-container" style={{ marginBottom: '24px' }}>
                  <h4 style={{ marginBottom: '16px', color: 'var(--color-dark-blue)' }}>Select Your Plan</h4>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {stripePrices.map((price) => (
                      <div
                        key={price.id}
                        onClick={() => setSelectedPrice(price.id)}
                        style={{
                          border: selectedPrice === price.id ? '2px solid var(--color-primary)' : '1px solid #ddd',
                          borderRadius: '8px',
                          padding: '16px 24px',
                          cursor: 'pointer',
                          backgroundColor: selectedPrice === price.id ? 'var(--color-light-background)' : 'white',
                          transition: 'all 0.2s',
                          minWidth: '140px',
                          textAlign: 'center'
                        }}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: 'var(--text-lg)', marginBottom: '4px' }}>
                          {price.interval === 'year' ? 'Annual' : 'Monthly'}
                        </div>
                        <div style={{ fontSize: 'var(--text-2xl)', color: 'var(--color-primary)' }}>
                          {formatPrice(price.unitAmount, price.currency, price.interval)}
                        </div>
                        {price.interval === 'year' && (
                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginTop: '4px' }}>
                            Save ~{Math.round((1 - (price.unitAmount / 12) / (stripePrices.find(p => p.interval === 'month')?.unitAmount || price.unitAmount / 12)) * 100)}%
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isEmailVerificationSent && !verificationFailed && (
                <>
                  <div className="input-container">
                    <input ref={firstNameRef} type="text" value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      onKeyDown={e => handleKeyPress(e, lastNameRef)}
                      aria-label="First name" placeholder="First name" autoComplete="given-name"
                      maxLength={100} required className="main-input" disabled={isLoading || isGoogleLoading} />
                  </div>
                  <div className="input-container">
                    <input ref={lastNameRef} type="text" value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      onKeyDown={e => handleKeyPress(e, emailRef)}
                      aria-label="Last name" placeholder="Last name" autoComplete="family-name"
                      maxLength={100} required className="main-input" disabled={isLoading || isGoogleLoading} />
                  </div>
                  <button onClick={handleGoogleSignUp} className="center-button secondary" disabled={isGoogleLoading || isLoading}>
                    {isGoogleLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <>
                        <GoogleIcon style={{ fontSize: "var(--text-sm)", marginRight: "12px", marginTop: "2px" }} />
                        Sign Up with Google
                      </>
                    )}
                  </button>

                  {!isGoogleSignupFlow && (
                    <div className="divider">
                      <span>Or, enter your email and password below</span>
                    </div>
                  )}

                  {isGoogleSignupFlow && (
                    <div style={{ textAlign: 'center', margin: '16px 0', padding: '12px', backgroundColor: 'var(--color-background-alt)', borderRadius: '8px' }}>
                      <p style={{ margin: 0, fontWeight: 500 }}>Signing up as: {email}</p>
                    </div>
                  )}

                  {!isGoogleSignupFlow && (
                    <>
                      <div className="input-container">
                        <input
                          ref={emailRef}
                          type="email"
                          value={email}
                          onChange={handleEmailChange}
                          onKeyDown={(e) => handleKeyPress(e, passwordRef)}
                          placeholder="Email"
                          className={`main-input ${emailValidation.isValid === false ? "input-error" : emailValidation.isValid === true ? "input-success" : ""}`}
                          disabled={isLoading || isInviteMode}
                        />
                        <div className="input-validation">
                          {emailValidation.isValid === true && <CircleCheck className="validation-icon success" />}
                          {emailValidation.isValid === false && <CircleX className="validation-icon error" />}
                        </div>
                      </div>
                      {emailValidation.message && <p className={`validation-message ${emailValidation.isValid ? "success" : "error"}`}>{emailValidation.message}</p>}
                    </>
                  )}


                  {!isGoogleSignupFlow && (
                    <>
                      <div className="input-container userback-ignore">
                        <input
                          ref={passwordRef}
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={handlePasswordChange}
                          onKeyDown={(e) => handleKeyPress(e, confirmPasswordRef)}
                          placeholder="Password"
                          className={`main-input ${passwordValidation.isValid === false ? "input-error" : passwordValidation.isValid === true ? "input-success" : ""}`}
                          disabled={isLoading}
                        />
                        <div className="input-validation">
                          <Tooltip title={showPassword ? "Hide password" : "Show password"} arrow>
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle">
                              {showPassword ? <EyeClosed /> : <Eye />}
                            </button>
                          </Tooltip>
                          {passwordValidation.isValid === true && <CircleCheck className="validation-icon success" />}
                          {passwordValidation.isValid === false && <CircleX className="validation-icon error" />}
                        </div>
                      </div>
                      {password && (
                        <div className="password-strength">
                          <div className="strength-bar">
                            <div className={`strength-fill strength-${passwordValidation.strength}`} style={{ width: `${(passwordValidation.strength / 5) * 100}%` }}></div>
                          </div>
                          <p className={`validation-message ${passwordValidation.isValid ? "success" : "error"}`}>{passwordValidation.message}</p>
                        </div>
                      )}

                      <div className="input-container">
                        <input
                          ref={confirmPasswordRef}
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          onKeyDown={(e) => handleKeyPress(e, phoneRef)}
                          placeholder="Confirm Password"
                          className={`main-input ${confirmPasswordValidation.isValid === false ? "input-error" : confirmPasswordValidation.isValid === true ? "input-success" : ""}`}
                          disabled={isLoading}
                        />
                        <div className="input-validation">
                          <Tooltip title={showConfirmPassword ? "Hide password" : "Show password"} arrow>
                            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="password-toggle">
                              {showConfirmPassword ? <EyeClosed /> : <Eye />}
                            </button>
                          </Tooltip>
                          {confirmPasswordValidation.isValid === true && <CircleCheck className="validation-icon success" />}
                          {confirmPasswordValidation.isValid === false && <CircleX className="validation-icon error" />}
                        </div>
                      </div>
                      {confirmPasswordValidation.message && <p className={`validation-message ${confirmPasswordValidation.isValid ? "success" : "error"}`}>{confirmPasswordValidation.message}</p>}

                      <hr />
                    </>
                  )}

                  <div className="input-container">
                    <input
                      ref={phoneRef}
                      type="tel"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      onKeyDown={(e) => handleKeyPress(e, null)}
                      placeholder="Phone Number (for account security)"
                      className={`main-input ${phoneValidation.isValid === false ? "input-error" : phoneValidation.isValid === true ? "input-success" : ""}`}
                      disabled={isLoading}
                    />
                    <div className="input-validation">
                      {phoneValidation.isValid === true && <CircleCheck className="validation-icon success" />}
                      {phoneValidation.isValid === false && <CircleX className="validation-icon error" />}
                    </div>
                  </div>
                  {phoneValidation.message && <p className={`validation-message ${phoneValidation.isValid ? "success" : "error"}`}>{phoneValidation.message}</p>}
                </>
              )}

              {errorMessage && <p className={messageType === "error" ? "login-error-message" : "login-success-message"}>{errorMessage}</p>}

              {!isEmailVerificationSent && !verificationFailed && (
                <>
                  {/* Show payment button for paying users who haven't paid yet */}
                  {billingPolicy.enabled && showPlanSelection && !stripeSessionData ? (
                    <button 
                      onClick={handleStripeCheckout} 
                      className="center-button primary" 
                      disabled={isStripeLoading || !emailValidation.isValid || !selectedPrice}
                    >
                      {isStripeLoading ? <CircularProgress size={20} color="inherit" /> : "Continue to Payment"}
                    </button>
                  ) : (
                    <button 
                      onClick={isGoogleSignupFlow ? handleGoogleSignUp : handleSignUp} 
                      className="center-button primary" 
                      disabled={isGoogleSignupFlow 
                        ? (isLoading || isGoogleLoading) 
                        : (isLoading || isGoogleLoading || !emailValidation.isValid || !passwordValidation.isValid || !confirmPasswordValidation.isValid || !phoneValidation.isValid)
                      }
                    >
                      {(isLoading || isGoogleLoading) ? <CircularProgress size={20} color="inherit" /> : (isGoogleSignupFlow ? "Complete Google Signup" : "Create Account")}
                    </button>
                  )}
                </>
              )}

              {verificationFailed && (
                <div className="verification-notice">
                  <h4>Your account was created</h4>
                  <p>Sign in with the email and password you just chose to request another verification email.</p>
                  <Link to="/" className="center-button primary">Sign in to resend verification email</Link>
                </div>
              )}

              {isEmailVerificationSent && (
                <div className="verification-notice">
                  <CircleCheck style={{ color: "var(--color-success)", marginBottom: 16 }} size={48} />
                  <h4>Account Created Successfully!</h4>
                  <p>
                    We've sent a verification link to <strong>{email}</strong>
                  </p>
                  <p>Please check your inbox (and spam/junk folder) and click the link to activate your account.</p>
                  <p style={{ marginTop: 16, fontSize: "var(--text-sm)", color: "var(--color-text-light)" }}>Redirecting to login page...</p>
                </div>
              )}
            </>
          )}

          {mfaPrompt}

          <footer className="footer">
            <p>
              &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
            </p>
            <p>
              Already have an account?{" "}
              <Link to="/" className="footer-link">
                Sign in here
              </Link>
            </p>
          </footer>
        </>
      )}
    </div>
  );
};

export default Signup;
